import React from 'react';

import { Alert } from 'src/design/atoms/Alert';
import { Breadcrumbs } from 'src/design/atoms/Breadcrumbs';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { TextInput } from 'src/design/atoms/TextInput';
import { DefaultFolderDto } from 'src/api/FiretrolApiClient';
import {
  useCreateDocumentTemplateFolder,
  useUpdateDocumentTemplateFolder,
} from 'src/api/queries/documentTemplates/useDocumentTemplateFolderMutations';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { getErrorMessage, showSuccessToast } from 'src/utils';

interface ICreateFolderDialogProps {
  selectedFolder: DefaultFolderDto | null;
  breadcrumbs: DefaultFolderDto[];
  onClose: () => void;
  edit?: boolean;
  templateId: string;
}

export const CreateFolderDialog: React.FC<ICreateFolderDialogProps> = ({
  selectedFolder,
  templateId,
  breadcrumbs,
  onClose,
  edit,
}) => {
  const createFolder = useCreateDocumentTemplateFolder();
  const updateFolder = useUpdateDocumentTemplateFolder();
  const [error, setError] = React.useState<string | null>(null);
  const [name, setName] = React.useState(
    selectedFolder && edit ? selectedFolder.name : '',
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      saveFolder();
    }
  };

  const saveFolder = async () => {
    setError(null);
    try {
      if (edit) {
        if (!selectedFolder) return;
        await updateFolder.mutateAsync({
          folderId: selectedFolder.id,
          request: {
            parentId: selectedFolder.parentId,
            masterId: selectedFolder.masterId,
            name,
          },
        });
        showSuccessToast(`"${name}" updated!`);
      } else {
        await createFolder.mutateAsync({
          templateId,
          request: {
            parentId: selectedFolder ? selectedFolder.id : null,
            masterId: selectedFolder
              ? selectedFolder.masterId
                ? selectedFolder.masterId
                : selectedFolder.id
              : null,
            name,
          },
        });
        showSuccessToast(`"${name}" created!`);
      }
      onClose();
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
  };

  const renderBreadcrumbs = () => {
    return breadcrumbs.map((crumb) => {
      return (
        <Text key={crumb.id} size="md">
          {crumb.name}
        </Text>
      );
    });
  };

  return (
    <Stack gap="md">
      <Stack gap="xs">
        <Text fw={600} c="black">
          Location
        </Text>
        <Breadcrumbs>
          <Text size="md" c="black">
            Documents
          </Text>
          {renderBreadcrumbs()}
        </Breadcrumbs>
      </Stack>
      <TextInput
        label="Name"
        autoFocus
        onKeyDown={handleKeyDown}
        autoComplete="off"
        placeholder="Folder name..."
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <Alert color="blue">
        Folder settings and permissions will be set on the job level
      </Alert>
      {error && <Text c="red">{error}</Text>}
      <Group justify="flex-end" mt="md">
        <SecondaryButton onClick={onClose}>Close</SecondaryButton>
        <BasicButton
          onClick={saveFolder}
          loading={createFolder.isPending || updateFolder.isPending}
          disabled={name.length < 2}
        >
          {edit ? 'Save Changes' : 'Create Folder'}
        </BasicButton>
      </Group>
    </Stack>
  );
};
